import React, { useState } from 'react';
import { libelleStatut, classeStatut, demandeUneAction } from '../../lib/statutsCommande';
import { getOrderLabel, urlDocument, emettreNoteDeCredit } from '../../services/api';

const OrderDetailsModal = ({ order, onClose }) => {
    const [impression, setImpression] = useState({ enCours: false, erreur: null });
    const [credit, setCredit] = useState({ enCours: false, erreur: null, note: null });

    if (!order) return null;

    /**
     * Ouvre le PDF de l'étiquette dans un nouvel onglet, prêt à imprimer.
     * Le serveur relaie le fichier depuis Sendcloud : le navigateur ne peut pas
     * aller le chercher lui-même, l'URL exigeant les clés API.
     */
    const imprimerEtiquette = async () => {
        setImpression({ enCours: true, erreur: null });
        try {
            const pdf = await getOrderLabel(order.id);
            const url = URL.createObjectURL(pdf);
            window.open(url, '_blank', 'noopener');
            // Laisser le temps à l'onglet de charger le blob avant de le libérer.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            setImpression({ enCours: false, erreur: null });
        } catch (e) {
            // La réponse d'erreur arrive en blob (responseType) : il faut la lire
            // pour retrouver le message du serveur.
            let message = "L'étiquette n'a pas pu être récupérée.";
            try {
                const texte = await e.response?.data?.text?.();
                if (texte) message = JSON.parse(texte).error || message;
            } catch {
                // On garde le message générique.
            }
            setImpression({ enCours: false, erreur: message });
        }
    };

    /**
     * Note de crédit TOTALE de la facture émise par le site (retour, annulation).
     * Le remboursement lui-même se fait dans Stripe, à part : ce bouton n'émet
     * que la pièce comptable, et une seule par facture.
     */
    const crediter = async () => {
        const motif = window.prompt(
            [
                `Émettre la note de crédit TOTALE de la facture ${order.factureNumero} ?`,
                'Elle est définitive (numéro JRNC-…). Le remboursement se fait séparément dans Stripe.',
                '',
                'Motif (facultatif) :',
            ].join(String.fromCharCode(10))
        );
        if (motif === null) return;
        setCredit({ enCours: true, erreur: null, note: null });
        try {
            const note = await emettreNoteDeCredit(order.id, motif);
            setCredit({ enCours: false, erreur: null, note });
        } catch (e) {
            setCredit({
                enCours: false,
                erreur: e.response?.data?.error || "La note de crédit n'a pas pu être émise.",
                note: null,
            });
        }
    };

    const noteDeCredit = credit.note
        ? { numero: credit.note.numero, chemin: credit.note.chemin }
        : order.noteDeCreditNumero
            ? { numero: order.noteDeCreditNumero, chemin: order.noteDeCreditChemin }
            : null;

    const ALERTES_FACTURE = {
        taux_par_defaut: "Stripe n'a indiqué aucun taux de TVA : la facture applique 21 % (Belgique).",
        taux_etranger: "Stripe a appliqué le taux d'un autre pays : à vérifier (seuil OSS, voir le dossier TVA).",
        detail_indisponible: "Le détail Stripe ne recoupait pas le montant encaissé : facture sur une seule ligne.",
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-gray-50/50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 m-0">Commande #{order.orderNumber}</h2>
                        <p className="text-sm text-gray-500 mt-1">{formatDate(order.createdAt)}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-8">
                    {/* Status & Customer */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gray-50 p-4 rounded-xl">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Statut</h3>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${classeStatut(order.status)}`}>
                                {libelleStatut(order.status)}
                            </span>
                            {demandeUneAction(order.status) && (
                                <p className="m-0 mt-2 text-xs text-red-700">
                                    Paiement encaissé, mais Sendcloud n'a pas créé l'étiquette.
                                    À faire à la main.
                                </p>
                            )}
                        </div>
                        <div className="bg-gray-50 p-4 rounded-xl">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Client</h3>
                            <p className="font-semibold text-gray-900">{order.user?.fullName}</p>
                            <p className="text-sm text-gray-500">{order.user?.email}</p>
                        </div>
                    </div>

                    {/* Items List */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                            🛒 Articles & Langues
                        </h3>
                        <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 text-xs text-gray-500 uppercase border-b border-gray-100">
                                    <tr>
                                        <th className="px-5 py-3 font-semibold">Produit</th>
                                        <th className="px-5 py-3 font-semibold text-center">Langue</th>
                                        <th className="px-5 py-3 font-semibold text-center">Qté</th>
                                        <th className="px-5 py-3 font-semibold text-right">Poids</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {order.items && order.items.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50/50">
                                            <td className="px-5 py-4 font-medium text-gray-900">{item.name}</td>
                                            <td className="px-5 py-4 text-center">
                                                {item.language === 'NL' ? (
                                                    <span className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-800 px-2.5 py-1 rounded-lg text-xs font-bold border border-orange-200">
                                                        🇳🇱 NL
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-200">
                                                        🇫🇷 FR
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 text-center text-gray-600">x{item.quantity}</td>
                                            <td className="px-5 py-4 text-right text-gray-600 text-sm">
                                                {item.weight ? `${item.weight}kg` : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Détail des frais de port — la pièce qui permet de rapprocher
                        cette commande de la facture Sendcloud. Absent des commandes
                        antérieures à cette traçabilité : on n'affiche rien plutôt
                        que d'écrire 0 €, ce qui serait faux. */}
                    {order.shippingPaid !== null && order.shippingPaid !== undefined && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                                Frais de port
                            </h3>
                            <dl className="m-0 space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <dt className="text-gray-600">Payé par le client</dt>
                                    <dd className="m-0 font-semibold text-gray-900">
                                        {Number(order.shippingPaid).toFixed(2)} €
                                    </dd>
                                </div>
                                {Number(order.shippingParticipation) > 0 && (
                                    <div className="flex justify-between">
                                        <dt className="text-green-700">Pris en charge par Mirum Games</dt>
                                        <dd className="m-0 font-semibold text-green-700">
                                            − {Number(order.shippingParticipation).toFixed(2)} €
                                        </dd>
                                    </div>
                                )}
                                <div className="flex justify-between border-t border-gray-200 pt-1">
                                    <dt className="text-gray-600">Coût transporteur réel</dt>
                                    <dd className="m-0 font-semibold text-gray-900">
                                        {Number(order.shippingCarrierCost).toFixed(2)} €
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    )}

                    {/* Facture — émise par le site depuis le 23/09/2026 (série JR-AAAA-NNNN),
                        ou facture Stripe pour les commandes antérieures. */}
                    <div className="rounded-xl border border-gray-200 p-4">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                            Facture
                        </h3>
                        {order.factureNumero ? (
                            <>
                                <p className="m-0 mb-3 text-sm text-gray-900">
                                    N° <strong>{order.factureNumero}</strong>
                                </p>
                                {(order.factureAlertes || []).map((a) => (
                                    <p key={a} className="m-0 mb-2 text-xs font-semibold text-amber-800">
                                        ⚠️ {ALERTES_FACTURE[a] || a}
                                    </p>
                                ))}
                                <div className="flex flex-wrap gap-2">
                                    <a
                                        href={urlDocument(order.factureChemin)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-bold text-white no-underline hover:bg-black"
                                    >
                                        🖨️ Imprimer la facture
                                    </a>
                                    {noteDeCredit ? (
                                        <a
                                            href={urlDocument(noteDeCredit.chemin)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-bold text-gray-800 no-underline hover:bg-gray-50"
                                        >
                                            Note de crédit {noteDeCredit.numero}
                                        </a>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={crediter}
                                            disabled={credit.enCours}
                                            className="rounded-lg border border-red-300 px-4 py-2.5 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-40"
                                        >
                                            {credit.enCours ? 'Émission…' : 'Émettre une note de crédit'}
                                        </button>
                                    )}
                                </div>
                                {credit.erreur && (
                                    <p className="m-0 mt-2 text-xs font-semibold text-red-700">{credit.erreur}</p>
                                )}
                            </>
                        ) : order.factureStripeUrl ? (
                            <a
                                href={order.factureStripeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-700 underline"
                            >
                                Facture Stripe ({order.orderNumber})
                            </a>
                        ) : (
                            <p className="m-0 text-xs text-gray-500">
                                Pas de facture : commande non encaissée, ou antérieure au 23/09/2026
                                sans facture Stripe enregistrée.
                            </p>
                        )}
                    </div>

                    {/* Étiquette d'expédition */}
                    <div className="rounded-xl border border-gray-200 p-4">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                            Étiquette d'expédition
                        </h3>
                        <button
                            type="button"
                            onClick={imprimerEtiquette}
                            disabled={impression.enCours || !order.sendcloudParcelId}
                            className="rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-black disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            {impression.enCours ? 'Récupération…' : "🖨️ Imprimer l'étiquette"}
                        </button>
                        {!order.sendcloudParcelId && (
                            <p className="m-0 mt-2 text-xs text-gray-500">
                                Aucun colis Sendcloud pour cette commande : il n'y a pas encore
                                d'étiquette à imprimer.
                            </p>
                        )}
                        {impression.erreur && (
                            <p className="m-0 mt-2 text-xs font-semibold text-red-700">
                                {impression.erreur}
                            </p>
                        )}
                    </div>

                    {/* Total */}
                    <div className="flex justify-end pt-4 border-t border-gray-100">
                        <div className="text-right">
                            <span className="block text-sm text-gray-500 mb-1">Total Commande</span>
                            <span className="text-2xl font-black text-gray-900">{order.totalPrice.toFixed(2)} €</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;
